import HealthDataFilterForm, {
	HealthDataFilterFormValues,
} from './HealthDataFilterForm';
import useHealthDataSource from '../../Hooks/useHealthDataSource';
import { HealthData } from '../../Enums/HealthData.enum';
import { HealthDataType } from '../../Enums/HealthDataType.enum';
import { baseHealthDataFilterSchemeLatam } from './BaseHealthDataFilter/BaseHealthDataFilterSchemeLatam';
import { getOneWeekBeforeDate } from '../../../../Utils/dates.util';
import React, { useEffect, useState } from 'react';
import useHealthDataFilter from '../../Hooks/useHealthDataFilter';
import { useAppContext } from '../../../../Services/AppContext';
import { baseHealthDataFilterSchemeDefault } from './BaseHealthDataFilter/BaseHealthDataFilterSchemeDefault';

interface BaseHealthDataFilterFormProps {
	onSubmit: (values: HealthDataFilterFormValues) => void;
}

const defValues = {
	from: getOneWeekBeforeDate(new Date()),
	to: new Date(),
	date: new Date(),
	view: 'week',
	dataType: HealthDataType.DEFAULT,
	toggleBloodGlucose: false,
	toggleBloodPressure: false,
	toggleWeight: false,
	toggleHeight: false,
	toggleWaistline: false,
	toggleSteps: true,
	toggleBreathingRate: false,
	toggleOxygenSaturation: false,
	toggleSleep: false,
	toggleHemoglobin: false,
	toggleHemoglobinA1C: false,
	toggleStressLevel: false,
	toggleActiveEnergy: false,
	toggleDistanceWalkingRunning: false,
	toggleWorkout: false,
	source: {
		appleHealth: true,
		googleFit: true,
		questionnaire: true,
		bioScan: true,
	},
};

const BaseHealthDataFilterForm: React.FC<
	BaseHealthDataFilterFormProps
> = props => {
	const { onSubmit } = props;
	const { mapSources } = useHealthDataSource();
	const { getPrepareFilterData } = useHealthDataFilter();
	const { globalTemplate } = useAppContext();
	const [defaultValues, setDefaultValues] = useState(defValues);

	useEffect(() => {
		handleOnSubmit(defaultValues);
	}, []);

	const handleOnSubmit = (values: any) => {
		setDefaultValues(values);
		const keys: string[] = [];
		if (values.toggleBloodPressure) {
			keys.push(
				HealthData.BLOOD_PRESSURE_SYSTOLIC,
				HealthData.BLOOD_PRESSURE_DIASTOLIC,
				HealthData.HEARTH_RATE,
			);
		}
		if (values.toggleWeight) {
			keys.push(HealthData.WEIGHT);
		}
		if (values.toggleWaistline) {
			keys.push(HealthData.WAISTLINE);
		}
		if (values.toggleBloodGlucose) {
			keys.push(HealthData.BLOOD_GLUCOSE);
		}
		if (values.toggleSteps) {
			keys.push(HealthData.STEP_COUNT);
		}
		if (values.toggleBreathingRate) {
			keys.push(HealthData.BREATHING_RATE);
		}
		if (values.toggleOxygenSaturation) {
			keys.push(HealthData.OXYGEN_SATURATION);
		}
		if (values.toggleSleep) {
			keys.push(HealthData.SLEEP);
		}
		if (values.toggleHemoglobin) {
			keys.push(HealthData.HEMOGLOBIN);
		}
		if (values.toggleHemoglobinA1C) {
			keys.push(HealthData.HEMOGLOBIN_A1C);
		}
		if (values.toggleStressLevel) {
			keys.push(HealthData.STRESS_LEVEL);
		}
		if (values.toggleActiveEnergy) {
			keys.push(HealthData.ACTIVE_ENERGY);
		}
		if (values.toggleDistanceWalkingRunning) {
			keys.push(HealthData.DISTANCE_WALKING_RUNNING);
		}
		if (values.toggleHeight) {
			keys.push(HealthData.HEIGHT);
		}
		if (values.toggleWorkout) {
			keys.push(HealthData.WORKOUT);
		}

		const dataForFilter = getPrepareFilterData(
			values.view,
			values.date,
			values.from,
			values.to,
		);

		const filterValues: HealthDataFilterFormValues = {
			from: dataForFilter.from,
			to: dataForFilter.to,
			view: dataForFilter.view,
			source: mapSources(values.source),
			type:
				dataForFilter.view === 'day' ? values.dataType : HealthDataType.DEFAULT,
			keys: keys,
		};

		onSubmit(filterValues);
	};

	let scheme;

	switch (globalTemplate) {
		case 'peru':
		case 'ecuador':
		case 'mexico':
		case 'colombia':
			scheme = baseHealthDataFilterSchemeLatam;
			break;
		case 'slovakia':
		case 'czechia':
		default:
			scheme = baseHealthDataFilterSchemeDefault;
	}

	return (
		<HealthDataFilterForm
			onSubmit={handleOnSubmit}
			formScheme={scheme}
			defaultValues={defaultValues}
		/>
	);
};

export default BaseHealthDataFilterForm;
